/**
 * Gallery Slider Roll Module
 *
 * Converts a WordPress core gallery block that carries the class
 * `.swiper-slider-roll` into a continuous auto-scrolling Swiper carousel.
 *
 * Expected markup (generated by WordPress core/gallery block):
 *   <figure class="wp-block-gallery swiper-slider-roll …">
 *     <figure class="wp-block-image …"><img …></figure>
 *     …
 *   </figure>
 *
 * The module wraps the inner `<figure>` slides with the Swiper-required
 * `.swiper-wrapper` div and individual `.swiper-slide` divs at runtime so the
 * original block markup needs no changes.
 *
 * @package EVT Restaurant Builder
 * @since 1.0.0
 */

/**
 * Initialise all `.swiper-slider-roll` gallery instances on the page.
 */
export function initGallerySliderRoll() {
	const galleries = document.querySelectorAll( '.wp-block-gallery.swiper-slider-roll' );

	if ( ! galleries.length ) {
		return;
	}

	if ( typeof Swiper === 'undefined' ) {
		console.warn( 'Swiper library not loaded. Gallery slider roll will display as static content.' );
		return;
	}

	galleries.forEach( ( gallery ) => {
		// Remove Gutenberg column count classes to avoid conflicting gallery layout styles.
		gallery.classList.forEach( ( className ) => {
			if ( /^columns-\d+$/.test( className ) ) {
				gallery.classList.remove( className );
			}
		} );

		// Collect existing slide figures before mutating the DOM.
		const slides = Array.from( gallery.querySelectorAll( ':scope > .wp-block-image' ) );

		if ( ! slides.length ) {
			return;
		}

		// Build Swiper wrapper and relocate slides into it.
		const wrapper = document.createElement( 'div' );
		wrapper.classList.add( 'swiper-wrapper' );

		slides.forEach( ( slide ) => {
			const swiperSlide = document.createElement( 'div' );
			swiperSlide.classList.add( 'swiper-slide' );
			swiperSlide.appendChild( slide );
			wrapper.appendChild( swiperSlide );
		} );

		// Clear the gallery and insert the Swiper structure (preserve any core gallery caption).
 		const caption = gallery.querySelector( ':scope > figcaption, :scope > .blocks-gallery-caption' );
 		gallery.classList.add( 'swiper' );
		gallery.innerHTML = '';
		gallery.appendChild( wrapper );

		if ( caption ) {
			gallery.appendChild( caption );
		}

		const desktopSlidesPerView = 7;
		const mobileSlidesPerView = 3;
		const initialSlidesPerView = window.innerWidth < 768 ? mobileSlidesPerView : desktopSlidesPerView;
		const preCloneCount = Math.ceil( initialSlidesPerView / 2 );
		const minSlideCountForLoop = Math.max( initialSlidesPerView + preCloneCount, initialSlidesPerView * 3 );

		// If source slides are fewer than required for a looping carousel,
		// duplicate slides up-front to prevent trailing whitespace at loop edges.
		if ( wrapper.children.length < minSlideCountForLoop ) {
			const baseSlides = Array.from( wrapper.children );
			let duplicateIndex = 0;

			while ( wrapper.children.length < minSlideCountForLoop ) {
				const sourceSlide = baseSlides[ duplicateIndex % baseSlides.length ];
				wrapper.appendChild( sourceSlide.cloneNode( true ) );
				duplicateIndex++;
			}
		}

		// Initialise Swiper in continuous-loop / marquee-roll mode.
		new Swiper( gallery, {
			loop: true,
			loopAddBlankSlides: false,
			slidesPerView: initialSlidesPerView,
			breakpoints: {
				0: {
					slidesPerView: mobileSlidesPerView,
				},
				768: {
					slidesPerView: desktopSlidesPerView,
				},
			},
			// Preload extra loop clones so no empty tail appears near the end.
			loopAdditionalSlides: initialSlidesPerView,
			loopedSlides: Math.min( wrapper.children.length, initialSlidesPerView * 2 ),
			slidesPerGroup: 1,
			spaceBetween: 0,
			centeredSlides: true,
			speed: 4000,
			autoplay: window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
 				? false
 				: {
 					delay: 2500,
 					disableOnInteraction: false,
 					pauseOnMouseEnter: true,
 				},
			grabCursor: true,
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},
			a11y: {
				enabled: true,
			},
		} );
	} );
}
